import React from 'react';
import {useQuery} from '@tanstack/react-query';
import {useNavigate} from 'react-router';

import {useExperimentsStore} from 'apps/experiments/src/store';
import {addToast} from 'common/components/toast-area/toast-manager';
import {ToastType} from 'common/components/toast/types';
import {getReportUrl} from 'common/report/util';
import {experimentQueryKeyForEntity} from 'apps/experiments/src/react-query';
import {useExperimentsApi} from 'apps/experiments/src/api/experiments';

import {PageTrackingEvent, trackExperimentPageAction} from '../tracking';

type FetchExperimentParams = {
  projectId: number;
  workspaceId: number;
  experimentId: string;
};

export function useQueryExperiment({projectId, workspaceId, experimentId}: FetchExperimentParams) {
  const experimentSetState = useExperimentsStore((state) => state.experimentSetState);
  const experimentClearHistory = useExperimentsStore((state) => state.experimentClearHistory);
  const setExperimentLoading = useExperimentsStore((state) => state.setExperimentLoading);
  const reset = useExperimentsStore((state) => state.reset);
  const navigate = useNavigate();

  const {getExperiment} = useExperimentsApi({
    projectId,
    workspaceId,
  });

  const {data, isLoading, isError, error, isSuccess} = useQuery({
    queryKey: experimentQueryKeyForEntity(projectId, workspaceId, experimentId),
    queryFn: async () => {
      reset();
      experimentClearHistory();
      return getExperiment({id: experimentId});
    },
    enabled: !!experimentId,
  });

  React.useEffect(() => {
    if (isError) {
      addToast({
        message: error.message,
        type: ToastType.ERROR,
      });
      navigate(
        getReportUrl(`experiments`, {
          projectId,
          workspaceId,
        }),
      );
    }
  }, [isError, error]);

  React.useEffect(() => {
    experimentClearHistory();
    if (isSuccess && data) {
      experimentSetState(data, false);
      trackExperimentPageAction({
        event: PageTrackingEvent.PageLoaded,
        experiment: data,
      });
    }
  }, [isSuccess, data]);

  React.useEffect(() => {
    setExperimentLoading(isLoading);
  }, [isLoading]);
}
